import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UserCheck, Send, Loader2, ShieldCheck } from "lucide-react";

export const MiningClaimantPortal = () => {
  const qc = useQueryClient();
  const [sub, setSub] = useState({
    claimant_name: "", claim_reference: "",
    province: "Matabeleland South", commodity: "Gold",
    documents_uploaded: 0,
  });

  const ref = useQuery({ queryKey: ["mm-ref"], queryFn: async () => (await api.get("/cadastre360/migration/reference")).data });
  const list = useQuery({ queryKey: ["mm-claimant-list"], queryFn: async () => (await api.get("/cadastre360/migration/claimant/submissions")).data });

  const submit = useMutation({
    mutationFn: async () => (await api.post("/cadastre360/migration/claimant/submissions", sub)).data,
    onSuccess: () => {
      toast.success("Migration submission acknowledged");
      qc.invalidateQueries({ queryKey: ["mm-claimant-list"] });
      setSub({ ...sub, claim_reference: "" });
    },
    onError: (e) => toast.error(formatApiError(e)),
  });

  return (
    <div className="space-y-6" data-testid="page-mining-claimant-portal">
      <header>
        <div className="font-mono-cad text-[10px] uppercase tracking-[0.28em] text-[#7C93A5]">Claimant Migration Portal</div>
        <h1 className="font-display text-3xl font-bold text-[#F6F8FA] mt-1 flex items-center gap-2">
          <UserCheck className="text-[#C99A2E]" /> Claim Holder Self-Service
        </h1>
        <div className="text-[13px] text-[#B3C2CC] mt-1 max-w-4xl leading-relaxed">
          Existing claim holders may register historical mining claim records into the migration staging environment. Claimants
          cannot access the internal claim register; all submissions await ministry review.
        </div>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="cad-panel p-5">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">New migration submission</div>
          <div className="space-y-2 mt-3">
            <input value={sub.claimant_name} onChange={(e) => setSub({ ...sub, claimant_name: e.target.value })}
                   placeholder="Claimant name or entity" data-testid="mcp-claimant"
                   className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            <input value={sub.claim_reference} onChange={(e) => setSub({ ...sub, claim_reference: e.target.value })}
                   placeholder="Historical claim reference" data-testid="mcp-ref"
                   className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            <div className="grid grid-cols-2 gap-2">
              <select value={sub.province} onChange={(e) => setSub({ ...sub, province: e.target.value })} data-testid="mcp-province"
                      className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {(ref.data?.provinces || []).map((p) => (<option key={p} value={p}>{p}</option>))}
              </select>
              <select value={sub.commodity} onChange={(e) => setSub({ ...sub, commodity: e.target.value })} data-testid="mcp-commodity"
                      className="bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]">
                {(ref.data?.commodities || []).map((c) => (<option key={c} value={c}>{c}</option>))}
              </select>
            </div>
            <input type="number" min="0" value={sub.documents_uploaded}
                   onChange={(e) => setSub({ ...sub, documents_uploaded: parseInt(e.target.value || "0") })}
                   placeholder="Number of supporting documents" data-testid="mcp-docs"
                   className="w-full bg-[#071B2B] border cad-hairline rounded-sm px-2 py-2 text-[13px] text-[#F6F8FA]" />
            <button onClick={() => submit.mutate()} disabled={!sub.claimant_name || !sub.claim_reference || submit.isPending}
                    data-testid="mcp-submit"
                    className="cad-btn-primary w-full py-2 rounded-sm text-[13px] inline-flex items-center justify-center gap-1.5">
              {submit.isPending ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />} Send to migration queue
            </button>
          </div>
          <div className="mt-4 border-t cad-hairline pt-3">
            <div className="font-mono-cad text-[10px] uppercase tracking-[0.2em] text-[#7C93A5]">Claimant self-service capabilities</div>
            <ul className="mt-2 space-y-1 text-[12px] text-[#DCE3E8]">
              {["Create a migration submission", "Enter claim details", "Upload old survey coordinates",
                "Upload survey diagrams", "Upload supporting documents", "View validation errors",
                "Correct incomplete submissions", "Track review status", "Respond to ministry queries",
                "Receive notifications", "Download acknowledgement receipts"].map((c) => (
                <li key={c} className="flex items-center gap-1.5"><ShieldCheck size={11} className="text-[#6BB88A]" />{c}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="cad-panel p-5 lg:col-span-2">
          <div className="font-mono-cad text-[10px] uppercase tracking-[0.24em] text-[#C99A2E]">Recent claimant submissions</div>
          <table className="w-full mt-3 text-[12.5px]">
            <thead><tr className="text-left border-b cad-hairline">
              {["Claimant", "Reference", "Province", "Commodity", "Docs", "Status", "Submitted"].map((h) => (
                <th key={h} className="px-2 py-1.5 font-mono-cad text-[10px] uppercase tracking-[0.16em] text-[#7C93A5]">{h}</th>
              ))}
            </tr></thead>
            <tbody className="cad-tbody">
              {(list.data || []).map((c) => (
                <tr key={c.id} data-testid={`mcp-row-${c.id}`}>
                  <td className="px-2 py-1.5 text-[#F6F8FA]">{c.claimant_name}</td>
                  <td className="px-2 py-1.5 font-mono-cad text-[11px]">{c.claim_reference}</td>
                  <td className="px-2 py-1.5 text-[#B3C2CC]">{c.province}</td>
                  <td className="px-2 py-1.5 text-[#B3C2CC]">{c.commodity}</td>
                  <td className="px-2 py-1.5 font-mono-cad">{c.documents_uploaded}</td>
                  <td className="px-2 py-1.5"><span className="cad-chip cad-chip-gold text-[9px] uppercase">{c.status?.replace(/_/g, " ")}</span></td>
                  <td className="px-2 py-1.5 font-mono-cad text-[10px] text-[#B3C2CC]">{c.submitted_at?.slice(0, 10)}</td>
                </tr>
              ))}
              {(!list.data || list.data.length === 0) &&
                <tr><td colSpan={7} className="px-2 py-4 text-center italic text-[#7C93A5] text-[12px]">No claimant submissions yet</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
